import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { useGetOrderReviewQuery } from "shared/redux/endpoints/procurementApprovals"
import StatusBadge from "../StatusBadge"
import { Checkbox } from "../../ui"
import { DecisionButtons, allowed } from "../approvals/DecisionCard"
import { formatVND, formatDate, hasQuote, proposalTotal, NO_QUOTE_LABEL } from "../../constants"

const Field = ({ label, value }) => (
  <MDBox minWidth={0}>
    <MDTypography variant="caption" color="text" display="block">
      {label}
    </MDTypography>
    <MDTypography variant="button" fontWeight="medium" sx={{ display: "block", overflowWrap: "anywhere" }}>
      {value || "—"}
    </MDTypography>
  </MDBox>
)

const Block = ({ title, children }) => (
  <MDBox minWidth={0}>
    <MDTypography
      variant="caption"
      color="info"
      fontWeight="bold"
      textTransform="uppercase"
      display="block"
      sx={{ letterSpacing: "0.5px", mb: 0.5 }}
    >
      {title}
    </MDTypography>
    {children}
  </MDBox>
)

const Muted = ({ children }) => (
  <MDTypography variant="caption" color="text" display="block">
    {children}
  </MDTypography>
)

const MAX_LINES = 4

const STEP_ICON = { done: ["check_circle", "success"], current: ["hourglass_top", "info"] }

const stepApprovers = (step) => {
  const list = step.approverNames || step.approvers
  return Array.isArray(list) ? list.join(", ") : list || "—"
}

// Fetches its own order (GET /order-requests/{id}, cached per id) because the
// group detail only lists id/code/title/status/total per member.
const GroupOrderCard = ({ orderId, onPickAction, onViewDetail, selected: ticked, onToggleSelect }) => {
  const { data: order, isLoading, isError } = useGetOrderReviewQuery(orderId)

  if (isLoading || isError || !order) {
    return (
      <MDBox bgColor="white" borderRadius="lg" shadow="sm" p={2} mb={2}>
        <MDTypography variant="body2" color="text">
          {isLoading ? "Loading…" : "Couldn't load this order."}
        </MDTypography>
      </MDBox>
    )
  }

  const actions = allowed(order.allowedActions)
  const poCount = order.purchases?.length || 0
  const lines = order.lines || []
  const proposals = order.vendorProposals || []
  const selected = proposals.find((p) => p.isSelected)
  const steps = order.approvalProgress || []

  return (
    <MDBox
      bgColor="white"
      borderRadius="lg"
      shadow="sm"
      p={2}
      mb={2}
      display="flex"
      gap={2}
      flexWrap="wrap"
      alignItems="flex-start"
    >
      <MDBox flex={1} minWidth={280}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
          {onToggleSelect && (
            <Checkbox
              checked={ticked}
              disabled={!actions.includes("approve")}
              onChange={(v) => onToggleSelect(orderId, v)}
              aria-label={`Select ${order.orCode}`}
            />
          )}
          <MDTypography variant="h6">{order.orCode}</MDTypography>
          <MDTypography variant="button" color="text" sx={{ overflowWrap: "anywhere" }}>
            {order.title}
          </MDTypography>
          <StatusBadge status={order.status} />
        </MDBox>
        <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} columnGap={3} rowGap={1.5}>
          <Field
            label="Requester"
            value={[order.requesterName, order.requesterCenter].filter(Boolean).join(" · ")}
          />
          <Field label="Total amount" value={formatVND(order.total)} />
          <Field label="Category" value={order.categoryName} />
          <Field label="Request date" value={formatDate(order.requestDate)} />
          <Field label="Region · Urgency" value={[order.region, order.urgency].filter(Boolean).join(" · ")} />
          <Field
            label="Lines · Proposals · POs"
            value={`${order.lines?.length || 0} · ${order.vendorProposals?.length || 0} · ${poCount}`}
          />
        </MDBox>
        <MDBox
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          columnGap={3}
          rowGap={1.5}
          mt={2}
          pt={1.5}
          borderTop="1px solid #e0e0e0"
        >
          <Block title={`Items (${lines.length})`}>
            {lines.length === 0 && <Muted>—</Muted>}
            {lines.slice(0, MAX_LINES).map((l, i) => (
              <MDTypography key={l.id ?? i} variant="caption" color="text" display="block">
                {l.itemName || l.offCatalogItemName || "—"} × {l.quantity ?? "?"}
                {l.unitPrice != null ? ` · ${formatVND(l.unitPrice)}` : ""}
              </MDTypography>
            ))}
            {lines.length > MAX_LINES && <Muted>+{lines.length - MAX_LINES} more</Muted>}
          </Block>
          <Block title="Selected proposal">
            {selected ? (
              <>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {selected.supplierName || selected.supplier?.name || "—"}
                </MDTypography>
                <Muted>
                  {hasQuote(selected) ? formatVND(proposalTotal(selected)) : NO_QUOTE_LABEL}
                  {proposals.length > 1 ? ` · ${proposals.length - 1} other proposal(s)` : ""}
                </Muted>
              </>
            ) : (
              <Muted>{proposals.length ? `${proposals.length} proposal(s), none selected` : "No proposals"}</Muted>
            )}
          </Block>
          <MDBox gridColumn={{ md: "1 / -1" }}>
            <Block title="Approval progress">
              {steps.length === 0 && <Muted>No approval route resolved yet.</Muted>}
              <MDBox display="flex" flexWrap="wrap" columnGap={3} rowGap={0.5}>
                {steps.map((step, i) => {
                  const [icon, color] = STEP_ICON[step.state] || ["radio_button_unchecked", "secondary"]
                  const last = (step.actions || []).slice(-1)[0]
                  return (
                    <MDBox key={step.stepOrder ?? step.step ?? i} display="flex" alignItems="center" gap={0.5}>
                      <MDTypography variant="caption" color={color} sx={{ display: "flex" }}>
                        <Icon fontSize="small">{icon}</Icon>
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {stepApprovers(step)}
                        {last ? ` — ${last.actor || "Someone"} ${last.action || last.type}` : ""}
                      </MDTypography>
                    </MDBox>
                  )
                })}
              </MDBox>
            </Block>
          </MDBox>
        </MDBox>
      </MDBox>
      <MDBox display="flex" flexDirection="column" gap={1} minWidth={150}>
        {actions.length > 0 && <DecisionButtons actions={actions} onPick={(a) => onPickAction(order, a)} />}
        <MDButton variant="outlined" color="info" size="small" onClick={() => onViewDetail(order.id)}>
          View details
        </MDButton>
      </MDBox>
    </MDBox>
  )
}

export default GroupOrderCard
